import { Text, Carousel, Spacing } from '@dls/web';
import { CardComponent } from '@singtel-web/widgets';
import React, { useState } from 'react';
import { ServiceAddonType } from '../../types/common.types';
import { formatBTLPrice } from '../../helpers/common';
import { ACTION_TYPES } from '../../constants/actions';
import { useDispatch } from 'react-redux';

export type BTLAddonsCarouselProps = {
  serviceAddons: ServiceAddonType[];
};

export const BTLAddonsCarousel = ({
  serviceAddons
}: BTLAddonsCarouselProps) => {
  const dispatch = useDispatch();
  // Track loading status for each addon by id
  const [loadingStatus, setLoadingStatus] = useState<{
    [id: string]: 'success' | 'failure' | undefined;
  }>(() =>
    serviceAddons.reduce((acc, addon) => {
      acc[addon?.id] =
        addon?.loading === 'success' || addon?.loading === 'failure'
          ? addon?.loading
          : undefined;
      return acc;
    }, {} as { [id: string]: 'success' | 'failure' | undefined })
  );

  const handleGetClick = (addon: ServiceAddonType) => {
    setLoadingStatus(prev => ({
      ...prev,
      [addon.id]: prev[addon.id] === 'success' ? 'failure' : 'success'
    }));

    // Prepare updated addon list with current loading statuses
    const updatedAddons = serviceAddons.map(a => ({
      ...a,
      loading:
        a.id === addon.id
          ? loadingStatus[a.id] === 'success'
            ? 'failure'
            : 'success'
          : loadingStatus[a.id]
    }));

    dispatch({
      type: ACTION_TYPES.ADDONS.BTL_ADDON_UPDATE,
      payload: {
        addonData: updatedAddons
      }
    });
  };

  if (!serviceAddons || serviceAddons.length === 0) {
    return null;
  }

  return (
    <>
      <Spacing bottom={1}>
        <Text tag="h4" type="header">
          Extra add-ons
        </Text>
      </Spacing>
      <Carousel
        type="card"
        swimlaneChildrenConfig={{ sm: 1, md: 2, lg: 3, xl: 3 }}
        data-testid="service-addons-carousel"
      >
        {serviceAddons.map(addon => {
          const loading = loadingStatus[addon.id];

          let actualPrice: string | undefined;
          let usualPrice: string | undefined;

          // Fallback to parent's price if no selected group addon or standalone addon
          if (!actualPrice) {
            actualPrice = addon?.actualMonthlyPrice;
            usualPrice = addon?.usualMonthlyPrice ?? undefined;
          }

          // First preference to actualMonthlyPrice, then oneTimePayment
          const isMonthlyPrice = !!actualPrice;
          const priceToDisplay = actualPrice || addon?.oneTimePayment;

          // Format the price with appropriate suffix ('/mth' for monthly, none for one-time)
          const footerPriceFormatted = formatBTLPrice({
            price: priceToDisplay as string,
            suffix: isMonthlyPrice ? '/mth' : ''
          });

          const usualPriceNum = parseFloat(usualPrice || '0');
          const footerRightSecondaryText =
            isMonthlyPrice && usualPriceNum > 0
              ? `${formatBTLPrice({
                  price: usualPrice as string,
                  suffix: '/mth'
                })}`
              : '';

          const footerLeftText = addon.contractTerm;

          return (
            <CardComponent
              data-testid={`carousel-service-addon-${addon.id}`}
              variant="thumbnailCard"
              footerVariant={3}
              image={{
                src: { url: addon.image, alt: addon.title }
              }}
              title={addon.title}
              isShadow
              isRoundedCorner
              subtitleContent={addon.promoText}
              footerLeftText={footerLeftText}
              footerRightText={footerPriceFormatted}
              ctaType="secondary"
              ctaText="Get"
              loading={loading}
              showDivider={true}
              footerButtonOnClick={() => handleGetClick(addon)}
              titleType="boldBody"
              footerRightSecondaryText={footerRightSecondaryText}
            />
          );
        })}
      </Carousel>
    </>
  );
};

export default BTLAddonsCarousel;
